import { AddRounded } from '@mui/icons-material';
import { Button } from '@sortlist-frontend/design-system/legacy';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { ReactNode, useEffect, useState } from 'react';

import { DraggableList } from '_components/common/DraggableList';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';
import { Service } from '_types/generic';

import { ServiceForm } from './ServiceForm/ServiceForm';

const NEW_ITEM_ID = 1000000000000000;

type ServicesProps = {
  locale: string;
  error: string;
  services: Service[];
  onChange: (services: Service[]) => void;
};

export const Services = ({ locale, error, services, onChange }: ServicesProps) => {
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);

  const [items, setItems] = useState<{ id: number; children: ReactNode }[] | undefined>([]);
  const [addingNewService, setAddingNewService] = useState(false);

  const [updatePosition, setUpdatePosition] = useState<
    undefined | { id: number; position: number; orderedIds: number[] }
  >();

  const removeNewService = () => {
    setAddingNewService(false);
    const newItems = items?.filter((item) => item.id !== NEW_ITEM_ID);
    setItems(newItems);
  };

  const addNewService = () => {
    setAddingNewService(true);
    const emptyItem = {
      id: NEW_ITEM_ID,
      children: (
        <ServiceForm
          selectedServices={selectedServices}
          locale={locale}
          opened={true}
          onClose={removeNewService}
          onSave={(service: Partial<Service>) => {
            setSelectedServices([...selectedServices, service as Service]);
          }}
          onDelete={() => {}}
        />
      ),
    };
    items && setItems([...items, emptyItem]);
  };

  const afterUpdate = () => {
    setAddingNewService(false);
    const items = selectedServices?.map((service, key) => {
      return {
        id: service?.expertise_id,
        children: (
          <ServiceForm
            selectedServices={selectedServices}
            locale={locale}
            key={key}
            service={service}
            onSave={(service: Partial<Service>) => {
              const newServices = selectedServices.map((serv, innerKey) => {
                if (innerKey === key) {
                  return service;
                }
                return serv;
              }) as Service[];

              setSelectedServices([]);
              setSelectedServices([...newServices]);
            }}
            onDelete={(serviceId) => {
              const newServices = selectedServices.filter((serv) => serv.expertise_id !== serviceId);
              setSelectedServices([...newServices]);
            }}
          />
        ),
      };
    });
    setItems(items);
  };

  useEffect(() => {
    afterUpdate();
    onChange(selectedServices);
  }, [selectedServices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (services && services.length > 0) {
      setSelectedServices(services);
    }
  }, [services]);

  useEffect(() => {
    if (updatePosition?.id) {
      const updatedServicesPosition = updatePosition.orderedIds.map((id, index) => {
        const service = selectedServices.find((serv) => serv.expertise_id === id) as Service;
        service.position = index + 1;
        return service;
      });
      setSelectedServices([...updatedServicesPosition]);
    }
  }, [updatePosition]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="px-8" data-testid="services">
      <div className="layout-column layout-align-start-end">
        {items && items.length > 0 && !addingNewService && (
          <Button
            size={'xs'}
            buttonVariant="light"
            buttonStyle="secondary"
            label={t('agency:claim.form.services.addService')}
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              addNewService();
            }}
            data-testid={'add-service'}
            iconLeft={<AddRounded style={{ fontSize: 16 }} />}
            id="add-service-btn"
          />
        )}
        <DraggableList
          placeholder={
            <div className="p-4 py-8 text-center">
              <p className="small medium">No service added yet.</p>
              <Button
                size="xs"
                type="submit"
                className={'mt-2'}
                onClick={(e) => {
                  e.preventDefault();
                  addNewService();
                }}
                buttonStyle="secondary"
                buttonVariant="light"
                data-testid={'add-service'}
                label={t('agency:claim.form.services.addService')}
                iconLeft={<AddRounded style={{ fontSize: 16 }} />}
                id="submit-service-btn"
              />
            </div>
          }
          className="width-100 mt-2"
          items={items}
          onChange={(update) => {
            setUpdatePosition(update);
          }}
          disabled={addingNewService}
          data-testid={'draggable-list'}
        />
      </div>
      {error && <p className="small text-error mt-2 display-inline-block">{error}</p>}
    </div>
  );
};
